import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MOBILE_BREAKPOINT, convertToVwMob } from '../../Utils/responsive';
import { PATHS } from '../../routes/paths';
import { useAppSelector } from '../../store/hooks';
import { selectSelectedProject } from '../../Module/Project/Presentation/projectSelectors';

import postIcon from '../../assets/Icon/sidbar/post_icon.png';
import postActiveIcon from '../../assets/Icon/sidbar/post_active_icon.png';
import sponsorIcon from '../../assets/Icon/sidbar/sponsor_icon.png';
import sponsorActiveIcon from '../../assets/Icon/sidbar/sponsor_active_icon.png';
import payIcon from '../../assets/Icon/sidbar/pay_icon.png';
import payActiveIcon from '../../assets/Icon/sidbar/pay_active_icon.png';
import projectIcon from '../../assets/Icon/sidbar/project_icon.png';
import projectActiveIcon from '../../assets/Icon/sidbar/project_active_icon.png';

const PROJECT_PATHS = [
  PATHS.POST_MANAGEMENT,
  PATHS.DONOR_MANAGEMENT,
  PATHS.PAYMENT_STATUS,
  PATHS.PROJECT_SETTINGS,
] as const;

const MobileBottomNav = () => {
  const { t } = useTranslation('translation');
  const navigate = useNavigate();
  const location = useLocation();
  const selectedProject = useAppSelector(selectSelectedProject);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= MOBILE_BREAKPOINT);

  const projectId = useMemo(() => {
    const fromState = (location.state as { projectId?: string } | undefined)?.projectId;
    if (fromState) return fromState;
    return selectedProject?.project_id;
  }, [location.state, selectedProject?.project_id]);

  const navItems = [
    {
      label: t('common.mobileBottomNav.post'),
      icon: postIcon,
      activeIcon: postActiveIcon,
      path: PATHS.POST_MANAGEMENT,
    },
    {
      label: t('common.mobileBottomNav.donors'),
      icon: sponsorIcon,
      activeIcon: sponsorActiveIcon,
      path: PATHS.DONOR_MANAGEMENT,
    },
    {
      label: t('common.mobileBottomNav.payment'),
      icon: payIcon,
      activeIcon: payActiveIcon,
      path: PATHS.PAYMENT_STATUS,
    },
    {
      label: t('common.mobileBottomNav.setting'),
      icon: projectIcon,
      activeIcon: projectActiveIcon,
      path: PATHS.PROJECT_SETTINGS,
    },
  ];

  const isActive = (path: string) => location.pathname === path;
  const isHidden =
    isMobile && (location.pathname === PATHS.LOGIN || location.pathname === PATHS.LOGIN_EMAIL || location.pathname === PATHS.SIGNUP || location.pathname === PATHS.PROJECT_MANAGEMENT);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (isHidden) return null;

  const handleNavClick = (path: string) => {
    const isProjectPath = PROJECT_PATHS.includes(path as (typeof PROJECT_PATHS)[number]);
    const navState = isProjectPath && projectId ? { state: { projectId } } : undefined;
    navigate(path, navState);
  };

  return (
    <NavWrapper>
      {navItems.map((item) => (
        <NavItem key={item.path} onClick={() => handleNavClick(item.path)} active={isActive(item.path)}>
          <Icon $src={isActive(item.path) ? item.activeIcon : item.icon} $active={isActive(item.path)} />
          <Label active={isActive(item.path)}>{item.label}</Label>
        </NavItem>
      ))}
    </NavWrapper>
  );
};

const NavWrapper = styled.nav`
  position: relative;
  flex: 0 0 auto;
  width: 100%;
  padding: 14px 22px calc(27px + env(safe-area-inset-bottom)) 22px;
  background-color: ${({ theme }) => theme.colors.grayScale[0]};
  border-top: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  z-index: 1000;
`;

const NavItem = styled.div<{ active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(4)};
  cursor: pointer;
  flex: 1;
  min-width: 0;
  text-align: center;
`;

const Icon = styled.div<{ $src: string; $active: boolean }>`
  width: ${convertToVwMob(30)};
  height: ${convertToVwMob(30)};
  background-color: ${({ theme, $active }) => ($active ? theme.colors.primaryBlue[40] : theme.colors.grayScale[70])};
  mask-image: url(${({ $src }) => $src});
  mask-size: contain;
  mask-repeat: no-repeat;
  mask-position: center;
  -webkit-mask-image: url(${({ $src }) => $src});
  -webkit-mask-size: contain;
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: center;
`;

const Label = styled.span<{ active: boolean }>`
  display: block;
  width: 100%;
  font-size: ${convertToVwMob(12)};
  font-weight: 500;
  line-height: 1.2;
  color: ${({ theme, active }) => (active ? theme.colors.primaryBlue[40] : theme.colors.grayScale[70])};
  text-align: center;
  white-space: nowrap;
`;

export default MobileBottomNav;

